import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";

import DashboardLayout from "../../components/DashboardLayout";
import { BrandAvatar } from "../../components/patterns/AdaptBrandSelector";
import { setCurrentBrand } from "../../redux/brand/brandSlice";
import { getPlatform } from "../../utils/platforms";

const STATUS_META = {
  completed: { label: "Ready", className: "bg-[#e2f2e6] text-[#2f7d4f]" },
  populating: { label: "Analyzing", className: "bg-[#f7e4d0] text-[#a15211]" },
  pending: { label: "Queued", className: "bg-theme-secondary text-theme-text opacity-80" },
  failed: { label: "Failed", className: "bg-[#fdf5f4] text-[#b3261e]" },
};

const displayDomain = (domain) => {
  if (!domain) return "";
  try {
    const url = domain.startsWith("http") ? new URL(domain) : new URL(`https://${domain}`);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return String(domain).replace(/^https?:\/\//i, "").replace(/^www\./, "").split("/")[0];
  }
};

const Brands = ({ currentUser }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brands, ready: brandsReady, currentBrand } = useSelector((state) => state.brand);

  const openBrand = (brand) => {
    dispatch(setCurrentBrand(brand));
    navigate("/brand/");
  };

  return (
    <DashboardLayout
      title="Your brands"
      currentUser={currentUser}
      primaryAction={(
        <Link
          to="/brand/new/"
          className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button"
        >
          Add brand
        </Link>
      )}
    >
      {!brandsReady ? (
        <p className="text-sm text-theme-text opacity-60">Loading brands...</p>
      ) : brands.length === 0 ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
          <BrandAvatar brand={null} size="lg" fallback="fingerprint" />
          <h2 className="mt-5 text-lg font-bold text-theme-text">Add your first brand</h2>
          <p className="mt-1.5 max-w-md text-[13px] leading-6 text-theme-text opacity-60">
            Connect a domain or social account and MoonScroll will analyze voice,
            audiences, and content patterns.
          </p>
          <Link
            to="/brand/new/"
            className="mt-5 inline-block rounded-lg bg-theme-primary px-6 py-2.5 text-sm font-semibold text-theme-button"
          >
            Add brand
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((brand) => {
            const status = STATUS_META[brand.populating_status] || STATUS_META.pending;
            const domainLabel = displayDomain(brand.domain);
            const creators = Array.isArray(brand.creators) ? brand.creators : [];
            const isActive = currentBrand?.hash === brand.hash;
            const subtitle = brand.tagline || brand.industry || brand.description;

            return (
              <article
                key={brand.hash}
                className={`overflow-hidden rounded-2xl border bg-theme-main transition hover:border-theme-primary ${
                  isActive ? "border-theme-primary" : "border-theme-secondary"
                }`}
              >
                <button
                  type="button"
                  onClick={() => openBrand(brand)}
                  className="relative flex h-36 w-full flex-col items-center justify-center overflow-hidden border-b border-theme-secondary bg-theme-secondary p-4 text-center"
                >
                  {/* Soft identity field: stacked creator faces behind the mark */}
                  <div className="pointer-events-none absolute inset-0 opacity-40">
                    <div className="absolute -left-6 top-6 h-28 w-28 rounded-full bg-theme-primary/15 blur-2xl" />
                    <div className="absolute -right-4 bottom-0 h-24 w-24 rounded-full bg-theme-primary/10 blur-2xl" />
                  </div>

                  <span
                    className={`absolute right-3 top-3 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${status.className}`}
                  >
                    {status.label}
                  </span>

                  <div className="relative flex flex-col items-center">
                    <div className="relative">
                      <BrandAvatar brand={brand} size="lg" fallback="initial" />
                      {creators.length > 0 && (
                        <div className="absolute -bottom-1 -right-1 flex -space-x-1.5">
                          {creators.slice(0, 3).map((creator) => {
                            const platform = getPlatform(creator.platform);
                            return (
                              <span
                                key={`${creator.platform}-${creator.platform_id}`}
                                title={platform?.name || creator.platform}
                                className="flex h-5 w-5 items-center justify-center rounded-full border border-theme-main bg-theme-main text-theme-text shadow-sm [&>svg]:h-3 [&>svg]:w-3"
                                style={platform?.color ? { color: platform.color } : undefined}
                              >
                                {platform?.icon || null}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {subtitle ? (
                      <p className="mt-2 line-clamp-2 max-w-[14rem] text-xs leading-4 text-theme-text opacity-60">
                        {subtitle}
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-theme-text opacity-50">
                        {brand.populating_status === "completed"
                          ? "Brand profile ready"
                          : "Profile still building"}
                      </p>
                    )}

                    <div className="mt-2 flex items-center gap-3 text-[11px] font-semibold text-theme-text opacity-60">
                      <span className="inline-flex items-center gap-1">
                        <LightBulbIcon className="h-3.5 w-3.5" />
                        {brand.idea_count ?? 0}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <DocumentTextIcon className="h-3.5 w-3.5" />
                        {brand.script_count ?? 0}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FilmIcon className="h-3.5 w-3.5" />
                        {brand.video_count ?? 0}
                      </span>
                    </div>
                  </div>
                </button>

                <div className="p-3.5">
                  <button
                    type="button"
                    onClick={() => openBrand(brand)}
                    className="block w-full text-left"
                  >
                    <h2 className="truncate text-[14.5px] font-bold text-theme-text">
                      {brand.name || "Unnamed brand"}
                    </h2>
                  </button>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                    {domainLabel ? (
                      <a
                        href={brand.domain?.startsWith("http") ? brand.domain : `https://${brand.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="inline-flex min-w-0 items-center gap-1 truncate text-xs text-theme-primary hover:underline"
                      >
                        <GlobeAltIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
                        <span className="truncate">{domainLabel}</span>
                      </a>
                    ) : creators[0] ? (
                      <span className="truncate text-xs text-theme-text opacity-60">
                        @{String(creators[0].platform_id || "").replace(/^@/, "")}
                      </span>
                    ) : (
                      <span className="text-xs text-theme-text opacity-50">No domain yet</span>
                    )}
                    {isActive && (
                      <span className="ml-auto shrink-0 rounded-full bg-theme-primary/15 px-2 py-0.5 text-[10px] font-semibold text-theme-primary">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
};

export default Brands;
